// FileUploadComponent.jsx (same component, adds a target toggle + backend upload)
import { useEffect, useState } from "react";
import "./FileUploadComponent.scss";
import { Chip, Dialog, DialogTitle, DialogContent, LinearProgress, DialogActions } from "@mui/material";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import app from "../../utils/firebase.js";
import { showToast } from "../../toastService.js";
import { useSelector } from "react-redux";
import newRequest from "../../utils/newRequest.js";
import { CustomLoadingButton } from "../ui/CustomButton.jsx";
import PublishIcon from "@mui/icons-material/Publish";
import DOMPurify from "dompurify";
import { t } from "i18next";
import { MdCloudUpload } from "react-icons/md";
import { useTranslation } from "react-i18next";

const FileUploadComponent = ({
    documentNo,
    documentId,
    documentType,
    fetchDocumentAttachments,
    uploadFolder,
    defaultUploadTarget = "backend", // "firebase" | "backend"
}) => {
    const { t } = useTranslation();
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);

    const [uploadTarget, setUploadTarget] = useState(defaultUploadTarget);
    const [isDragging, setIsDragging] = useState(false);

    const filesLimit = 5;
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileName, setCurrentFileName] = useState("");
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const [uploadButtonTitle, setUploadButtonTitle] = useState("Upload");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        const newFiles = droppedFiles.map((file, index) => ({
            id: selectedFiles.length + index,
            name: file.name,
            file,
        }));

        if (selectedFiles.length + newFiles.length <= filesLimit) {
            setSelectedFiles([...selectedFiles, ...newFiles]);
        } else {
            setNotifyDialogTitle("Note!");
            setNotifyDialogText(`Please select a maximum of <strong>${filesLimit} files</strong> at a time.`);
            setIsNotifyDialogOpen(true);
        }
    };

    // Keep your existing DB save for firebase uploads (backend route can do its own DB save; see below)
    const saveFileToDb = async (
        fl_name,
        fl_internal_name,
        fl_type,
        fl_path,
        fl_category,
        fl_size_kb,
        fl_parent_doc_id,
        fl_folder,
        fl_public_url
    ) => {
        try {
            await newRequest.post("/files/add", {
                fl_name,
                fl_internal_name,
                fl_type,
                fl_path,
                fl_category,
                fl_doc_type: documentType,
                fl_doc_no: documentNo,
                fl_doc_index: "",
                fl_doc_sub_index: "",
                fl_ent_code: "",
                fl_aent_code: "",
                fl_user_id: "",
                fl_size_kb,
                fl_parent_doc_id,
                fl_source: uploadTarget,
                fl_folder,
                fl_public_url
            });
        } catch (error) {
            console.error("Error saving file to DB:", error);
        }
    };

    const handleSelectedFiles = (e) => {
        const files = Array.from(e.target.files);
        const newFiles = files.map((file, index) => ({
            id: selectedFiles.length + index,
            name: file.name,
            file,
        }));

        if (selectedFiles.length + newFiles.length <= filesLimit) {
            setSelectedFiles([...selectedFiles, ...newFiles]);
        } else {
            setNotifyDialogTitle("Note!");
            setNotifyDialogText(`Please select a maximum of <strong>${filesLimit} files</strong> at a time.`);
            setIsNotifyDialogOpen(true);
        }
    };

    const handleRemoveFile = (id) => {
        setSelectedFiles(selectedFiles.filter((attachment) => attachment.id !== id));
    };

    const uploadToFirebase = async (file, fileFolder) => {
        const storage = getStorage(app);
        const fileName = new Date().getTime() + file.name;
        const fileExtension = file.name.split(".").pop();
        const fileSizeKB = file.size / 1024;

        const storageRef = ref(storage, `qc_${currentUser.usr_org_code}/${fileFolder}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(Math.round(progress));
                },
                (error) => {
                    setIsUploadDialogOpen(false);
                    reject(error);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                    await saveFileToDb(
                        file.name,
                        fileName,
                        fileExtension,
                        downloadURL,
                        documentType,
                        fileSizeKB,
                        documentId,
                        uploadFolder,
                        downloadURL
                    );

                    fetchDocumentAttachments(documentType, documentId);
                    resolve();
                }
            );
        });
    };

    // ---------- Upload to backend (multipart/form-data) ----------
    const uploadToBackend = async (file, fileFolder) => {
        const formData = new FormData();
        // metadata the backend can use to organize + save DB record
        formData.append("orgCode", currentUser.usr_org_code);
        formData.append("folder", fileFolder);
        formData.append("documentType", documentType);
        formData.append("documentNo", documentNo);
        formData.append("documentId", documentId);
        formData.append("userCode", currentUser.usr_id);

        // IMPORTANT append file LAST
        formData.append("file", file);

        // optional: progress bar (axios supports onUploadProgress)
        const res = await newRequest.post("/uploadFiles/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
                if (!evt.total) return;
                const progress = (evt.loaded / evt.total) * 100;
                setUploadProgress(Math.round(progress));
            },
        });

        // if backend saves DB record itself, just refresh list:
        fetchDocumentAttachments(documentType, documentId);

        return res.data;
    };

    const uploadAllFiles = async () => {
        try {
            setIsUploadDialogOpen(true);
            setUploadButtonTitle("Please Wait...");

            for (let i = 0; i < selectedFiles.length; i++) {
                const attachment = selectedFiles[i];
                setCurrentFileName(attachment.name);
                setCurrentFileIndex(i + 1);
                setUploadProgress(0);

                if (uploadTarget === "firebase") {
                    await uploadToFirebase(attachment.file, uploadFolder);
                } else {
                    await uploadToBackend(attachment.file, uploadFolder);
                }
            }

            const noOfFiles = selectedFiles?.length;
            const singleFileName = noOfFiles === 1 ? selectedFiles[0].name : "";

            setSelectedFiles([]);
            setIsUploadDialogOpen(false);

            showToast(
                `${noOfFiles === 1 ? `${singleFileName} successfully uploaded!` : `${noOfFiles} files successfully uploaded!`}`,
                "success"
            );
        } catch (error) {
            console.log(error);
        } finally {
            setUploadButtonTitle("Upload");
        }
    };

    useEffect(() => setTotalFiles(selectedFiles.length), [selectedFiles]);

    return (
        <div className="main-wrapper">
            {/* Toggle */}
            {/* <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={uploadTarget}
                    onChange={(e, v) => v && setUploadTarget(v)}
                >
                    <ToggleButton value="firebase">Firebase</ToggleButton>
                    <ToggleButton value="backend">Backend</ToggleButton>
                </ToggleButtonGroup>
                <button
                    type="button"
                    // className={`set__auto ${autoTopup ? "is-on" : ""}`}
                    className={`two-face-toggle is-on`}
                    onClick={() => setUploadTarget((val) => val === "BACKEND" ? "FIREBASE" : "BACKEND")}
                >
                    {uploadTarget === "BACKEND" ? "FIREBASE" : "BACKEND"}
                </button>
            </div> */}
            <div
                className={`drag-drop-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="icon-wrapper">
                    <MdCloudUpload className="icon" />
                </div>

                <h1 className="main-title">Drag and drop files here</h1>
                <p className="sub-title">
                    Pick upto five files at a time, max 100MB per file
                </p>

                <label htmlFor="file-upload" className="browse-btn">
                    {t('browseFiles')}
                </label>
            </div>

            <div className="attachment-upload">
                <input type="file" multiple id="file-upload" style={{ display: "none" }} onChange={handleSelectedFiles} />
                <div className="attachments">
                    {selectedFiles.map((attachment, index) => (
                        <Chip
                            key={attachment.id}
                            label={attachment.name}
                            onDelete={() => handleRemoveFile(attachment.id)}
                            className={`${currentFileIndex === index ? "attachment-chip current" : "attachment-chip"}`}
                        />
                    ))}

                    {selectedFiles?.length > 0 && (
                        <CustomLoadingButton
                            currentTheme={currentTheme}
                            iconPosition="end"
                            endIcon={<PublishIcon />}
                            onClick={uploadAllFiles}
                            height={"32px"}
                            width={"max-content"}
                            borderRadiusType="both"
                            variant="tint"
                            color="#f55656"
                            tooltip={selectedFiles?.length > 1 ? t('uploadFilesNo', { param1: selectedFiles?.length }) + " " + t('files') : `Upload ${selectedFiles[0].name}`}
                        >
                            {uploadButtonTitle}{" "}
                            {selectedFiles?.length === 1 ? selectedFiles?.length + " file" : selectedFiles?.length + " files"}
                        </CustomLoadingButton>
                    )}
                </div>
            </div>

            <UploadDialog
                open={isUploadDialogOpen}
                handleClose={() => setIsUploadDialogOpen(false)}
                uploadProgress={uploadProgress}
                currentFileName={currentFileName}
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
            />

            <Dialog open={isNotifyDialogOpen} onClose={() => setIsNotifyDialogOpen(false)}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notifyDialogText) }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={() => setIsNotifyDialogOpen(false)}>{t("close")}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const UploadDialog = ({ open, handleClose, uploadProgress, currentFileName, currentFileIndex, totalFiles }) => {
    const { currentTheme } = useSelector((state) => state.theme);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                    color: currentTheme === "dark" ? "#fff" : "#000",
                },
            }}
            slotProps={{
                backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } },
            }}
        >
            <DialogTitle>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span
                        style={{
                            padding: "1px 5px",
                            borderRadius: "5px",
                            width: "max-content",
                            fontWeight: "400",
                            backgroundColor: currentTheme === "dark" ? "#2B3139" : "#F7F9FC",
                            color: currentTheme === "dark" ? "#fff" : "#000",
                        }}
                    >
                        {`Uploading ${currentFileIndex} of ${totalFiles}`}
                    </span>
                    <span
                        style={{
                            padding: "1px 5px",
                            borderRadius: "5px",
                            width: "max-content",
                            fontWeight: "400",
                            backgroundColor: currentTheme === "dark" ? "#2B3139" : "#F7F9FC",
                            color: currentTheme === "dark" ? "#fff" : "#000",
                        }}
                    >
                        {uploadProgress === 0 ? "Starting..." : <p>{uploadProgress}% Completed</p>}
                    </span>
                </div>
            </DialogTitle>
            <DialogContent>
                <div style={{ width: "100%", overflowWrap: "break-word" }}>
                    <p style={{ fontWeight: "400", fontSize: "18px" }}>{currentFileName}</p>
                    <div style={{ borderRadius: "8px", margin: "10px 0px" }}>
                        <LinearProgress variant="determinate" value={uploadProgress} style={{ borderRadius: "8px" }} />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FileUploadComponent;
